/* The panel's own toast: a pill that slides up from the corner, used wherever a
 * change saves in the background and the screen has already moved on.
 *
 * It lived inside the kanban folder, which is why the board had it and the Gantt
 * — the other screen where you edit by dragging — fell back to antd's `message`
 * and looked like a different product mid-drag. */
let container = null;
let stylesInjected = false;

const ICONS = {
  success:
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none"><path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  error:
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  info:
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none"><path d="M12 8h.01M11 12h1v4h1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/></svg>',
};

function injectStyles() {
  if (stylesInjected) return;
  stylesInjected = true;
  const style = document.createElement("style");
  style.id = "ds-toast-styles";
  style.textContent = `
    .ds-toast-container {
      position: fixed;
      bottom: 24px;
      right: 24px;
      left: 24px;
      z-index: 2000;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 8px;
      pointer-events: none;
    }
    @media (max-width: 480px) {
      .ds-toast-container { right: 12px; left: 12px; bottom: 16px; }
    }
    .ds-toast {
      pointer-events: auto;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      max-width: min(90vw, 420px);
      padding: 9px 16px 9px 14px;
      border-radius: 999px;
      font-family: inherit;
      font-size: 13px;
      font-weight: 500;
      line-height: 1.4;
      color: var(--ds-text-primary, #0f172a);
      background: var(--ds-surface, #fff);
      border: 1px solid var(--ds-border, #e2e8f0);
      box-shadow: var(--ds-shadow-md, 0 6px 24px rgba(0,0,0,0.14));
      cursor: pointer;
      opacity: 0;
      transform: translateY(12px) scale(0.96);
      transition: opacity .22s cubic-bezier(.22,1,.36,1),
                  transform .28s cubic-bezier(.22,1,.36,1);
    }
    .ds-toast.is-visible { opacity: 1; transform: translateY(0) scale(1); }
    .ds-toast__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      flex-shrink: 0;
    }
    .ds-toast--success .ds-toast__icon { color: #16a34a; background: rgba(22,163,74,.12); }
    .ds-toast--error   .ds-toast__icon { color: #dc2626; background: rgba(220,38,38,.12); }
    .ds-toast--info    .ds-toast__icon { color: #2563eb; background: rgba(37,99,235,.12); }
    .ds-toast__text { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    @media (prefers-reduced-motion: reduce) {
      .ds-toast { transition: opacity .15s linear; transform: none; }
      .ds-toast.is-visible { transform: none; }
    }
  `;
  document.head.appendChild(style);
}

function getContainer() {
  injectStyles();
  if (container && document.body.contains(container)) return container;
  container = document.createElement("div");
  container.className = "ds-toast-container";
  document.body.appendChild(container);
  return container;
}

function show(type, content, duration) {
  if (!content) return;
  const root = getContainer();

  const el = document.createElement("div");
  el.className = `ds-toast ds-toast--${type}`;
  el.setAttribute("dir", "rtl");

  const icon = document.createElement("span");
  icon.className = "ds-toast__icon";
  icon.innerHTML = ICONS[type] || ICONS.info;

  const text = document.createElement("span");
  text.className = "ds-toast__text";
  text.textContent = content;

  el.appendChild(icon);
  el.appendChild(text);
  root.appendChild(el);

  requestAnimationFrame(() => el.classList.add("is-visible"));

  const remove = () => {
    if (el.dataset.removing) return;
    el.dataset.removing = "1";
    el.classList.remove("is-visible");
    setTimeout(() => el.remove(), 240);
  };

  const timer = setTimeout(remove, duration || (type === "error" ? 3200 : 2000));
  el.addEventListener("click", () => {
    clearTimeout(timer);
    remove();
  });
}

export const toast = {
  success: (content, duration) => show("success", content, duration),
  error: (content, duration) => show("error", content, duration),
  info: (content, duration) => show("info", content, duration),
};

export default toast;
